import * as React from "react";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import { Location } from "../../types/location";
import LoadingButton from "@mui/lab/LoadingButton";
import { BaseModal, StyledTextField } from "../../components/global";
import {
  useAddLocationMutation,
  useEditLocationMutation,
  useGetLocationByIdQuery,
} from "../../services/location.service";
import SelectCountry from "../../components/SelectCountry";
import SelectMapAddress from "../../components/SelectMapAddress";
import { AddressComponent } from "../../types/common";
import { useGetCountriesQuery } from "../../services/country.service";
import TitleDivider from "../../components/TitleDivider";
import PinDropIcon from "@mui/icons-material/PinDrop";
import SearchIcon from "@mui/icons-material/Search";
import CoordinateInfo from "../../components/CoordinateInfo";
import SelectState from "../../components/SelectState";
import { useUILabel } from "../../hooks/useUILabel";

interface LocationModalFormProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: number) => void;
  data: Location | null;
}

const LocationModalForm = ({
  open,
  onClose,
  onCreated,
  data,
}: LocationModalFormProps) => {
  const [add, { isLoading: adding }] = useAddLocationMutation();
  const [edit, { isLoading: editing }] = useEditLocationMutation();
  const { data: countries } = useGetCountriesQuery();
  const [showAddress, setShowAddress] = React.useState(false);
  const locationLabel = useUILabel("common.location");
  const siteNumberLabel = useUILabel("project.siteNumber");
  const clientNameLabel = useUILabel("project.clientName");
  const addressLabel = useUILabel("common.address");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const emailLabel = useUILabel("common.email");
  const createLabel = useUILabel("common.create");
  const updateLabel = useUILabel("common.update");
  const cancelLabel = useUILabel("common.cancel");
  const createLocationLabel = useUILabel("common.createLocation");
  const editLocationLabel = useUILabel("common.editLocation");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");
  const invalidLabel = useUILabel("common.invalid");
  const primaryContactLabel = useUILabel("location.primaryContact");
  const fullNameLabel = useUILabel("location.fullName");
  const address1Label = useUILabel("location.address1");
  const address2Label = useUILabel("location.address2");
  const stateLabel = useUILabel("common.state");
  const cityLabel = useUILabel("common.city");
  const zipLabel = useUILabel("common.zip");
  const searchNewAddressLabel = useUILabel("location.searchNewAddress");
  const enteredValueDoesNotMatchEmailFormatLabel = useUILabel("common.enteredValueDoesNotMatchEmailFormat");

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    setError,
    formState: { errors },
    watch,
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!payload.country) {
        setError("country", { type: "custom", message: invalidLabel });
        return;
      }

      if (!data) {
        const loc = await add(payload).unwrap();
        if (onCreated) {
          onCreated(loc.id);
        }
      } else {
        if (!payload.password) delete payload.password;
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (!open) return;

    setValue("name", data?.name);
    setValue("address_1", data?.address_1);
    setValue("address_2", data?.address_2);
    setValue("city", data?.city);
    setValue("state", data?.state);
    setValue("zip", data?.zip);
    setValue("country", data?.country_id || null);
    const [lng, lat] = data?.coordinate?.coordinates || [];
    setValue("lat", lat || 0);
    setValue("lng", lng || 0);
    setShowAddress(!!lat && !!lng);

    setValue("primary_contact_name", data?.primary_contact_name);
    setValue("primary_contact_title", data?.primary_contact_title);
    setValue(
      "primary_contact_phone_number",
      data?.primary_contact_phone_number
    );
    setValue("primary_contact_email", data?.primary_contact_email);
    setValue("site_number", data?.site_number);

    clearErrors();
  }, [data, open]);

  const handleMapAddressChange = (
    address: AddressComponent,
    lat?: number,
    lng?: number
  ) => {
    setValue("address_1", `${address.street_number} ${address.street}`);
    setValue("city", address.city);
    setValue("state", address.state);
    setValue("zip", address.postal_code);
    setValue("lat", lat);
    setValue("lng", lng);
    const found = countries?.find(
      (it) => it.code.toLowerCase() == address.country.toLowerCase()
    );
    setValue("country", found?.id);
    setShowAddress(true);
  };

  const formRef = React.useRef<HTMLFormElement>(null);

  const footer = (
    <>
      <Button onClick={onClose}>{cancelLabel}</Button>
      <LoadingButton
        onClick={() => formRef.current?.requestSubmit()}
        variant="contained"
        loading={adding || editing}
      >
        {!data ? createLabel : updateLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={!data ? createLocationLabel : editLocationLabel}
      footer={footer}
      maxWidth="sm"
    >
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ 
          p: 2.5, 
          display: "flex", 
          flexDirection: "column", 
          gap: 2, 
          maxHeight: "calc(100vh - 250px)", 
          overflowY: "auto",
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 2 },
            maxHeight: "calc(90vh - 140px)",
          },
        }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <StyledTextField
                label={siteNumberLabel}
                InputLabelProps={{ shrink: !!value }}
                value={value || ""}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.name}
                helperText={!!errors.name && thisFieldIsRequiredLabel}
              />
            )}
          />
          <Controller
            name="site_number"
            control={control}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <StyledTextField
                label={clientNameLabel}
                InputLabelProps={{ shrink: !!value }}
                value={value || ""}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          {!showAddress && (
            <SelectMapAddress
              onChange={handleMapAddressChange}
              error={!!errors.country}
              helperText={!!errors.country && thisFieldIsRequiredLabel}
            />
          )}
          {showAddress && (
            <Box>
              <TitleDivider title={addressLabel} />
              <Button
                onClick={() => {
                  setValue("country", null); //reset
                  setShowAddress(false);
                }}
                size="small"
                startIcon={<SearchIcon />}
              >
                {searchNewAddressLabel}
              </Button>
              <Controller
                name="address_1"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label={address1Label}
                    InputLabelProps={{ shrink: !!value }}
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.address_1}
                    helperText={!!errors.address_1 && thisFieldIsRequiredLabel}
                  />
                )}
              />
              <Controller
                name="address_2"
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label={address2Label}
                    InputLabelProps={{ shrink: !!value }}
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    sx={{ mt: 2 }}
                  />
                )}
              />
              <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry
                      onChange={onChange}
                      value={value}
                      sx={{ mt: 0 }}
                      error={!!errors.country}
                      helperText={!!errors.country && thisFieldIsRequiredLabel}
                    />
                  )}
                />
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <SelectState
                      label={stateLabel}
                      value={value || ""}
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      error={!!errors.state}
                      helperText={!!errors.state && thisFieldIsRequiredLabel}
                    />
                  )}
                />
              </Stack>
              <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
                <Controller
                  name="city"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label={cityLabel}
                      InputLabelProps={{ shrink: !!value }}
                      value={value || ""}
                      fullWidth
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.city}
                      helperText={!!errors.city && thisFieldIsRequiredLabel}
                    />
                  )}
                />
                <Controller
                  name="zip"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label={zipLabel}
                      InputLabelProps={{ shrink: !!value }}
                      value={value || ""}
                      sx={{ mt: 0 }}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.zip}
                      helperText={!!errors.zip && thisFieldIsRequiredLabel}
                    />
                  )}
                />
              </Stack>
              <CoordinateInfo lat={watch("lat")} lng={watch("lng")} />
            </Box>
          )}
          <Box>
            <TitleDivider title={primaryContactLabel} />
            <Stack direction={"row"} spacing={1} sx={{ mt: 2 }}>
              <Controller
                name="primary_contact_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label={fullNameLabel}
                    value={value || ""}
                    fullWidth
                    sx={{ mt: 0 }}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.primary_contact_name}
                    helperText={
                      !!errors.primary_contact_name && thisFieldIsRequiredLabel
                    }
                  />
                )}
              />
              {/* <Controller
                name="primary_contact_title"
                control={control}
                // rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <TextField
                    label="Title"
                    value={value}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    // error={!!errors.primary_contact_title}
                    // helperText={
                    //   !!errors.primary_contact_title && "This field is required"
                    // }
                  />
                )}
              /> */}
            </Stack>
            <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
              <Controller
                name="primary_contact_phone_number"
                control={control}
                rules={{ required: true, minLength: 6 }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label={phoneNumberLabel}
                    value={value || ""}
                    fullWidth
                    sx={{ mt: 0 }}
                    onChange={onChange}
                    onBlur={onBlur}
                    type="number"
                    error={!!errors.primary_contact_phone_number}
                    helperText={
                      !!errors.primary_contact_phone_number &&
                      thisFieldIsRequiredLabel
                    }
                  />
                )}
              />
              <Controller
                name="primary_contact_email"
                control={control}
                rules={{
                  required: false,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: enteredValueDoesNotMatchEmailFormatLabel,
                  },
                }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label={emailLabel}
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    type="email"
                    error={!!errors.primary_contact_email}
                    helperText={
                      !!errors.primary_contact_email && thisFieldIsRequiredLabel
                    }
                  />
                )}
              />
            </Stack>
          </Box>
        </Box>
      </form>
    </BaseModal>
  );
};
export default LocationModalForm;
